import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

export type ConfirmationDialogAction = 'archive' | 'delete' | 'restore' | 'confirm' | 'custom';

export interface ConfirmationDialogData {
  message?: string;
  actionType?: ConfirmationDialogAction;
  confirmButtonText?: string;
  cancelButtonText?: string;
  hideCancelButton?: boolean;
  params?: { [key: string]: any };
  // New fields for detailed layout
  title?: string;
  description?: string;
  layout?: 'simple' | 'detailed';
}

@Component({
  selector: 'app-confirmation-dialog',
  standalone: false,
  templateUrl: './confirmation-dialog.component.html',
  styleUrls: ['./confirmation-dialog.component.scss'],
})
export class ConfirmationDialogComponent {
  constructor(
    public dialogRef: MatDialogRef<ConfirmationDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ConfirmationDialogData
  ) {
    // Validate that either message or description is provided
    if (!this.data.message && !this.data.description) {
      throw new Error('ConfirmationDialogComponent: Either message or description must be provided');
    }

    // Set default layout to 'simple' if not provided
    if (!this.data.layout) {
      this.data.layout = 'simple';
    }

    // Set default action type if not provided
    if (!this.data.actionType) {
      this.data.actionType = 'custom';
    }
    // Set default button texts if not provided
    if (!this.data.cancelButtonText) {
      this.data.cancelButtonText = 'common.cancel';
    }
    if (!this.data.confirmButtonText) {
      this.data.confirmButtonText = this.getDefaultConfirmText();
    }
  }

  /**
   * Get default confirm button text based on action type
   */
  private getDefaultConfirmText(): string {
    switch (this.data.actionType) {
      case 'archive':
        return 'common.archive';
      case 'delete':
        return 'common.delete';
      case 'restore':
        return 'common.restore';
      case 'confirm':
        return 'common.confirm';
      default:
        return 'common.confirm';
    }
  }

  /**
   * Check if the dialog is using detailed layout
   */
  get isDetailedLayout(): boolean {
    return this.data.layout === 'detailed';
  }

  /**
   * Get the CSS class for the confirm button based on action type
   */
  get confirmButtonClass(): string {
    const actionType = this.data.actionType || 'custom';
    return `confirmation-dialog__button--${actionType}`;
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }

  onConfirm(): void {
    this.dialogRef.close(true);
  }
}
